/**
 * WP1.7 — deal outcomes (D1-5, D1-7). Moderators record the outcome
 * (open / won / lost), the lost reason and the stage dates; both companies
 * see the status and stage dates read-only (never the reason).
 * Backend: `MatchResult.outcome` (`outcome` on the wire).
 */
import i18n from "../i18n";
import { formatDate } from "../i18n/format";

export type DealOutcomeStatus = "open" | "won" | "lost";

/** The company-visible shape (`DealOutcomePublic`); the moderator shape extends it. */
export interface DealOutcomeView {
  status: DealOutcomeStatus;
  matchAcceptedAt: string | null;
  firstMeetingAt: string | null;
  proposalAt: string | null;
  contractSignedAt: string | null;
  lostAt: string | null;
}

/** Mirrors `DealLostReason` on the backend; labels live in `domain:outcome.lostReason`. */
export const LOST_REASONS = [
  "price",
  "terms",
  "product_fit",
  "compliance",
  "logistics",
  "timing",
  "unresponsive",
  "chose_other",
  "other",
] as const;

export type LostReason = (typeof LOST_REASONS)[number];

const isLostReason = (value: string | null | undefined): value is LostReason =>
  (LOST_REASONS as readonly string[]).includes(value ?? "");

export function lostReasonOptions(): { value: LostReason; label: string }[] {
  return LOST_REASONS.map((value) => ({ value, label: i18n.t(`domain:outcome.lostReason.${value}`) }));
}

export function outcomeVariant(status: string | null | undefined): "warning" | "success" | "danger" {
  return status === "won" ? "success" : status === "lost" ? "danger" : "warning";
}

export function outcomeLabel(status: string | null | undefined): string {
  const s: DealOutcomeStatus = status === "won" || status === "lost" ? status : "open";
  return i18n.t(`domain:outcome.status.${s}`);
}

export function lostReasonLabel(value: string | null | undefined): string {
  return isLostReason(value) ? i18n.t(`domain:outcome.lostReason.${value}`) : "—";
}

/** Stages in order (D1-7 tiered success); the last one is "lost" only for lost deals. */
export function outcomeStages(outcome: DealOutcomeView): { label: string; date: string | null }[] {
  const stages = [
    { label: i18n.t("domain:outcome.stage.matchAccepted"), date: outcome.matchAcceptedAt },
    { label: i18n.t("domain:outcome.stage.firstMeeting"), date: outcome.firstMeetingAt },
    { label: i18n.t("domain:outcome.stage.proposal"), date: outcome.proposalAt },
    { label: i18n.t("domain:outcome.stage.contractSigned"), date: outcome.contractSignedAt },
  ];
  if (outcome.status === "lost") stages.push({ label: i18n.t("domain:outcome.stage.lost"), date: outcome.lostAt });
  return stages;
}

export function formatOutcomeDate(value: string | null): string {
  return formatDate(value);
}
